import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePlanningPeriod } from "@/context/PlanningPeriodContext";
import { PlanningPeriodSelector } from "@/components/PlanningPeriodSelector";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, TrendingUp, TrendingDown, CheckCircle2 } from "lucide-react";

const STATUS_CFG = {
  over: { cls: "bg-amber-100 text-amber-800", Icon: TrendingUp, label: "Boven norm" },
  under: { cls: "bg-sky-100 text-sky-800", Icon: TrendingDown, label: "Onder norm" },
  ok: { cls: "bg-red-100 text-red-800", Icon: CheckCircle2, label: "In balans" },
};

export default function DistributionPage() {
  const { query } = usePlanningPeriod();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/statistics/distribution?${query}`)
      .then((r) => { if (active) setData(r.data); })
      .catch((e) => { if (active) toast.error(formatApiError(e.response?.data?.detail)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [query]);

  const cols = data ? Array.from(new Set(data.rows.flatMap((r) => Object.keys(r.by_type)))).sort() : [];
  const maxTotal = data ? Math.max(1, ...data.rows.map((r) => r.total)) : 1;

  return (
    <div className="space-y-6" data-testid="distribution-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Verdeling & statistiek</h1>
        <p className="text-muted-foreground mt-1">Eerlijke verdeling van diensten en werkzaamheden.</p>
      </div>

      <PlanningPeriodSelector />
      {loading || !data ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <>
          <Card className="p-4 flex flex-wrap gap-6 text-sm">
            <div><span className="text-muted-foreground">Gemiddelde:</span> <span className="font-bold font-heading">{data.average}</span> diensten</div>
            <div><span className="text-muted-foreground">Waarschuwing onder:</span> <span className="font-bold">{data.thresholds.under}%</span></div>
            <div><span className="text-muted-foreground">Waarschuwing boven:</span> <span className="font-bold">{data.thresholds.over}%</span></div>
          </Card>

          <Card className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vrijwilliger</TableHead>
                  <TableHead className="w-40">Totaal</TableHead>
                  {cols.map((c) => <TableHead key={c} className="text-center">{c}</TableHead>)}
                  <TableHead className="text-right">Afwijking</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => {
                  const cfg = STATUS_CFG[r.status];
                  return (
                    <TableRow key={r.user_id} data-testid={`dist-row-${r.user_id}`}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-bold font-heading w-6">{r.total}</span>
                          <Progress value={(r.total / maxTotal) * 100} className="h-2 w-24" />
                        </div>
                      </TableCell>
                      {cols.map((c) => <TableCell key={c} className="text-center text-sm">{r.by_type[c] || "–"}</TableCell>)}
                      <TableCell className={`text-right font-semibold ${r.deviation_pct > 0 ? "text-amber-600" : r.deviation_pct < 0 ? "text-sky-600" : "text-muted-foreground"}`}>
                        {r.deviation_pct > 0 ? "+" : ""}{r.deviation_pct}%
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${cfg.cls}`}>
                          <cfg.Icon className="w-3.5 h-3.5" /> {cfg.label}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}
